<p-toast />

<div class="company-profile">
  <header class="profile-hero">
    <div class="hero-top">
      <button pButton type="button" icon="pi pi-arrow-left" [label]="returnUrl ? 'Back to report' : 'Back'"
              severity="secondary" [text]="true" (click)="back()"></button>
      <div class="hero-actions">
        @if (returnUrl && isComplete && !dirty) {
          <button pButton type="button" label="Return to report" icon="pi pi-arrow-right" severity="success"
                  [outlined]="true" (click)="back()"></button>
        }
        <button pButton type="button" label="Save profile" icon="pi pi-save" severity="primary"
                (click)="save()" [loading]="saving" [disabled]="!dirty || saving || loading"></button>
      </div>
    </div>
    <div class="hero-body">
      <div>
        <p-tag value="Organization · One-time setup" severity="info" />
        <h1>Company profile</h1>
        <p class="hero-subtitle">
          Set this up <strong>once for your company</strong>. Every SSS, PhilHealth, Pag-IBIG, and BIR report — plus billing SOAs — uses these employer details automatically.
        </p>
      </div>
      @if (!loading) {
        <div class="hero-status" [class.hero-status--ready]="isComplete">
          <span class="hero-status-value">{{ completionPercent }}%</span>
          <span class="hero-status-label">{{ isComplete ? 'Ready to file' : 'Required fields' }}</span>
        </div>
      }
    </div>
  </header>

  @if (fromReport) {
    <section class="context-banner">
      <i class="pi pi-info-circle"></i>
      <div>
        <strong>You don't need to do this for every report.</strong>
        Complete the fields below once, save, then return to your report — all forms will pick up the same employer data.
      </div>
    </section>
  } @else {
    <section class="context-banner context-banner--neutral">
      <i class="pi pi-compass"></i>
      <div>
        This page is also listed in the <strong>Setup</strong> guide on first login. Admins should complete it before generating government remittance reports.
      </div>
    </section>
  }

  @if (loading) {
    <p-progressBar mode="indeterminate" [style]="{ height: '3px' }" />
  } @else {
    <div class="layout">
      <aside class="aside">
        <section class="panel readiness-panel">
          <h2>Filing checklist</h2>
          <ul class="checklist">
            @for (field of completionFields; track field.key) {
              <li [class.checklist--done]="field.value.trim()"
                  [class.checklist--required]="field.required && !field.value.trim()">
                <button type="button" class="checklist-btn" (click)="focusField(field.key)">
                  <i class="pi" [class.pi-check-circle]="field.value.trim()" [class.pi-circle]="!field.value.trim()"></i>
                  <span>{{ field.label }}</span>
                  @if (field.required) { <small>Required</small> }
                </button>
              </li>
            }
          </ul>
          @if (isComplete) {
            <p-tag severity="success" value="All reports can use this profile" styleClass="w-full" />
          } @else {
            <p-tag severity="warn" value="Finish required fields before filing" styleClass="w-full" />
          }
        </section>

        <section class="panel info-panel">
          <h2>Applies to all reports</h2>
          <ul>
            <li>SSS R-3, PhilHealth RF-1, Pag-IBIG MCRF</li>
            <li>BIR 1601-C, 2316, 1604-C</li>
            <li>Client billing SOA headers</li>
          </ul>
          <p class="info-note">
            SOA signature blocks are under <strong>Staffing → SOA Signatories</strong>.
            Employee SSS/TIN numbers are under <strong>HR → Employees</strong>.
          </p>
        </section>
      </aside>

      <main class="main">
        <section class="panel">
          <div class="panel-head">
            <h2>Company information</h2>
            <span class="panel-hint">Legal / trade name and contact</span>
          </div>
          <div class="form-grid">
            <div class="field field--wide">
              <label for="cp-name">Company name *</label>
              <input #fieldName id="cp-name" pInputText [(ngModel)]="companyName" (ngModelChange)="markDirty()"
                     placeholder="Legal or trade name" maxlength="200" />
            </div>
            <div class="field field--wide">
              <label for="cp-address">Registered address</label>
              <input #fieldAddress id="cp-address" pInputText [(ngModel)]="address" (ngModelChange)="markDirty()"
                     placeholder="Street, city, province" maxlength="500" />
            </div>
            <div class="field">
              <label for="cp-email">Email</label>
              <input #fieldEmail id="cp-email" pInputText [(ngModel)]="email" (ngModelChange)="markDirty()"
                     placeholder="finance@company.com" maxlength="100" />
            </div>
            <div class="field">
              <label for="cp-zip">ZIP code</label>
              <input #fieldZip id="cp-zip" pInputText [(ngModel)]="zipCode" (ngModelChange)="markDirty()"
                     maxlength="10" />
            </div>
          </div>
        </section>

        <section class="panel">
          <div class="panel-head">
            <h2>Government registration</h2>
            <span class="panel-hint">Employer numbers printed on remittance forms</span>
          </div>
          <div class="form-grid">
            <div class="field">
              <label for="cp-tin">BIR TIN *</label>
              <input #fieldTin id="cp-tin" pInputText [(ngModel)]="tin" (ngModelChange)="markDirty()"
                     placeholder="000-000-000-000" maxlength="30" />
            </div>
            <div class="field">
              <label for="cp-tin-branch">TIN branch code</label>
              <input id="cp-tin-branch" pInputText [(ngModel)]="tinBranchCode" (ngModelChange)="markDirty()"
                     placeholder="0000 (head office)" maxlength="4" />
            </div>
            <div class="field">
              <label for="cp-rdo">RDO code</label>
              <input #fieldRdo id="cp-rdo" pInputText [(ngModel)]="rdoCode" (ngModelChange)="markDirty()"
                     placeholder="e.g. 039" maxlength="10" />
            </div>
            <div class="field">
              <label for="cp-sss">SSS employer no. *</label>
              <input #fieldSss id="cp-sss" pInputText [(ngModel)]="sssNo" (ngModelChange)="markDirty()" maxlength="30" />
            </div>
            <div class="field">
              <label for="cp-ph">PhilHealth employer no. *</label>
              <input #fieldPh id="cp-ph" pInputText [(ngModel)]="philHealthNo" (ngModelChange)="markDirty()" maxlength="30" />
            </div>
            <div class="field">
              <label for="cp-pagibig">Pag-IBIG employer no. *</label>
              <input #fieldPagibig id="cp-pagibig" pInputText [(ngModel)]="pagIbigNo" (ngModelChange)="markDirty()" maxlength="30" />
            </div>
            <div class="field">
              <label for="cp-pagibig-branch">Pag-IBIG branch code</label>
              <input id="cp-pagibig-branch" pInputText [(ngModel)]="pagIbigBranchCode" (ngModelChange)="markDirty()"
                     placeholder="e.g. 01" maxlength="2" />
            </div>
          </div>
        </section>

        @if (returnUrl) {
          <div class="return-row">
            <button pButton type="button" label="Save and return to report" icon="pi pi-check"
                    severity="success" (click)="saveAndReturn()" [loading]="saving"
                    [disabled]="saving || loading"></button>
          </div>
        }
      </main>
    </div>
  }
</div>
